import { Box, Stack, Typography } from '@mui/material'
import { CHIP } from '~/components/Charts/chrome/chartChrome'
import { DataValue } from '~/components/Charts/chrome/DataValue'
import { GrowthChip } from '~/components/Charts/chrome/GrowthChip'

export interface PanelStat {
  prefix: string
  sum: number
  avg: number
  growth: number | null // null suppresses Δ
  /** Row 15-light: the grayed previous-period baseline cell — renders the zero-delta
   *  GrowthChip (the baseline IS the Δ reference, so its own delta is zero by definition). */
  baseline?: boolean
  /** Renders an empty grid cell (baseline slot with no pre-window data) — no prefix/Σ/ø/chip. */
  empty?: boolean
  /** Task 5 (chart-semantics wave): on a VT-heterogeneous selection there is no honest cross-VT
   *  total to fabricate — the cell renders one labeled Σ row per value type instead. When set,
   *  the aggregate Σ/ø/GrowthChip block is replaced entirely (sum/avg/growth on the parent
   *  PanelStat are ignored). Per-entry growth is null when there's no comparable prior sum. */
  perSeries?: Array<{ label: string; sum: number; growth: number | null }>
}

interface Props {
  stats: PanelStat[]
  /** index-0 cell shows 'Comparison Chart' chip. */
  isComparison?: boolean
  /** pl to clear the y-axis on cell 0 (default true). */
  leadOffset?: boolean
}

/** Wave-3 §2 (D3): panel-aligned stat row ABOVE the plot. Replaces the ControlLabel
 *  float — nothing overlaps the chart area anymore. Column count MUST equal the panel
 *  count of the sibling chart row (both are N equal columns/flex:1 with gap/spacing 1). */
export function PanelStatRow({ stats, isComparison = false, leadOffset = true }: Props) {
  return (
    <Box
      data-testid="panel-stat-row"
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${stats.length}, 1fr)`,
        gap: 1,
        mb: 0.5,
      }}
    >
      {stats.map((stat, index) => {
        return (
          <Box
            key={index}
            data-testid="panel-stat-cell"
            sx={{ minWidth: 0, pl: index === 0 && leadOffset ? 7 : 0.5 }}
          >
            {stat.empty ? null : (
              <>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ display: 'block' }}
                >
                  {stat.prefix}
                </Typography>
                {stat.perSeries ? (
                  <Stack spacing={0.25} data-testid="panel-stat-per-series">
                    {stat.perSeries.map((s) => (
                      <Stack key={s.label} direction="row" spacing={1} alignItems="baseline">
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ minWidth: 0 }}
                        >
                          {s.label}
                        </Typography>
                        <DataValue value={s.sum} glyph="Σ" size="sm" />
                        {s.growth !== null ? <GrowthChip growth={s.growth} /> : null}
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    alignItems="baseline"
                    data-testid="panel-stat-value-row"
                  >
                    <DataValue value={stat.sum} glyph="Σ" />
                    <DataValue value={stat.avg} glyph="ø" size="sm" />
                    {stat.baseline ? (
                      <GrowthChip growth={0} baseline />
                    ) : isComparison && index === 0 ? (
                      <Typography variant="caption" sx={{ color: 'primary.main' }} noWrap>
                        {CHIP.comparisonChart}
                      </Typography>
                    ) : index >= 1 && stat.growth !== null ? (
                      <GrowthChip growth={stat.growth} />
                    ) : null}
                  </Stack>
                )}
              </>
            )}
          </Box>
        )
      })}
    </Box>
  )
}
